// @ts-nocheck
import { AlertTriangle, ChevronRight, Lock, RefreshCw, Send, Timer } from 'lucide-react';
import { isValidEmail } from '../../../../../../utils/email/isValidEmail';
import { pctFmt } from '../shared/voteRoomFormat';
import { formatShutdownRuleSummary } from '../shared/voteRoomShutdown';
import { VoteRoomStatTile } from './VoteRoomStatTile';

/**
 * Fourth wizard step: final confirmation panel with eligible / excluded shareholder lists,
 * shutdown rule summary, and the launch action.
 */
export const LaunchVoteRoomStep = ({
  shareholders,
  eligibleShareholders,
  excludedShareholders,
  threshold75,
  eligibleShares,
  hasEmails,
  precheckReady,
  waiverReady = true,
  waiverReadiness = { required: false, ready: true, items: [] },
  waiverReadinessLoading = false,
  waiverReadinessError = null,
  onRefreshWaiverReadiness,
  shutdownSettings,
  locked,
  launching,
  launchError,
  onBack,
  onLaunch,
}) => {
  const shutdownSummary = formatShutdownRuleSummary(shutdownSettings);
  const pendingWaivers = (waiverReadiness?.items ?? []).filter((item) => item.status !== 'Completed');
  const canLaunch =
    !launching &&
    eligibleShareholders.length > 0 &&
    hasEmails &&
    precheckReady &&
    waiverReady &&
    !waiverReadinessLoading &&
    !waiverReadinessError;
  const launchStats = [
    { label: 'Total Shareholders', value: shareholders.length },
    { label: 'Eligible to Vote', value: eligibleShareholders.length, color: 'text-emerald-600' },
    { label: 'Excluded', value: excludedShareholders.length, color: 'text-amber-600' },
    { label: '75% Threshold', value: threshold75.toLocaleString(), color: 'text-emerald-600' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold text-gray-900">Confirm and Launch</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Review the final shareholder list below. After launch, the cap table is locked and DocuSign sends one
            envelope with the Shareholder Consent first, then the Disclosure Statement, to all eligible shareholders.
          </p>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {launchStats.map((s) => (
            <VoteRoomStatTile key={s.label} label={s.label} value={s.value} color={s.color} tone="flat" />
          ))}
        </div>

        <div className="flex gap-3 rounded-lg border border-red-100/90 bg-red-50/50 px-4 py-3">
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-red-100 bg-white text-red-600"
            aria-hidden
          >
            <Timer size={16} strokeWidth={2} />
          </div>
          <div className="flex flex-1 min-w-0 items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wider text-red-800/80">Auto-Close Policy</p>
              <p className="text-xs font-medium text-gray-900 mt-0.5">Shutdown Rule</p>
              <p className="text-xs font-normal text-gray-500 mt-0.5 inline-flex items-center gap-1">
                <Lock size={11} className="text-gray-400" aria-hidden />
                Locked after launch
              </p>
            </div>
            <p className="text-xs font-medium text-red-700 text-right shrink-0">{shutdownSummary}</p>
          </div>
        </div>

        <div className="border border-gray-100 rounded-md overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Eligible Shareholders</span>
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 tabular-nums">
              {eligibleShareholders.length}
            </span>
          </div>
          {eligibleShareholders.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-500 text-center">No eligible shareholders.</div>
          ) : (
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[36%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
              </colgroup>
              <thead className="bg-white border-b border-gray-100">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Shares
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    % of Eligible
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {eligibleShareholders.map((sh) => (
                  <tr key={sh.id}>
                    <td className="px-3 py-2 text-xs font-semibold text-gray-900 truncate" title={sh.name}>
                      {sh.name}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 truncate" title={sh.email || ''}>
                      {!sh.email ? (
                        <span className="text-red-400">Missing</span>
                      ) : isValidEmail(sh.email) ? (
                        sh.email
                      ) : (
                        <span className="text-red-400">Invalid</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 tabular-nums text-right">
                      {sh.shares.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 tabular-nums font-medium text-right">
                      {eligibleShares > 0 ? pctFmt(sh.shares / eligibleShares) : '0.0%'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {excludedShareholders.length > 0 && (
          <div className="border border-amber-100 rounded-md overflow-hidden bg-amber-50/30">
            <div className="bg-amber-50 px-3 py-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-amber-800">Excluded Shareholders</span>
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-white text-amber-700 ring-1 ring-amber-200 tabular-nums">
                {excludedShareholders.length}
              </span>
            </div>
            <div className="divide-y divide-amber-100">
              {excludedShareholders.map((sh) => (
                <div key={sh.id} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-900 truncate">{sh.name}</p>
                    <p className="text-xs text-amber-700 truncate">
                      {sh.exclusionType === 'dqi'
                        ? 'DQI'
                        : sh.exclusionType === '318'
                          ? `Section 318${sh.attributedTo ? ` — attributable to ${sh.attributedTo}` : ''}`
                          : 'Excluded'}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-amber-800 tabular-nums">
                    {sh.shares.toLocaleString()} shares
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {launchError && (
          <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2.5 text-xs text-red-700 flex items-start space-x-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-red-400" />
            <span>{launchError}</span>
          </div>
        )}

        {!precheckReady && (
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 text-xs text-amber-800 flex items-start space-x-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-500" />
            <span>All eligible shareholders must have verified email delivery before launch.</span>
          </div>
        )}

        {waiverReadinessError ? (
          <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2.5 text-xs text-red-700 flex items-start space-x-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-red-400" />
            <span>{waiverReadinessError}</span>
          </div>
        ) : null}

        {waiverReadiness?.required && !waiverReady && !waiverReadinessLoading ? (
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-xs text-amber-900 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start space-x-2 min-w-0">
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-500" />
                <div className="min-w-0">
                  <p className="font-semibold text-amber-950">280G Waivers Incomplete</p>
                  <p className="mt-0.5 text-amber-800">
                    Complete parachute payment waivers on the <strong>280G Waiver</strong> tab before launching.
                  </p>
                </div>
              </div>
              {onRefreshWaiverReadiness ? (
                <button
                  type="button"
                  onClick={() => void onRefreshWaiverReadiness()}
                  className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-semibold bg-white text-amber-800 border border-amber-200 hover:bg-amber-50 flex-shrink-0"
                >
                  <RefreshCw size={12} className="mr-1" />
                  Refresh
                </button>
              ) : null}
            </div>
            {pendingWaivers.length > 0 ? (
              <ul className="space-y-1 pl-5 list-disc text-amber-900">
                {pendingWaivers.map((item) => (
                  <li key={item.individualId}>
                    <span className="font-medium">{item.name || item.individualId}</span>
                    <span className="text-amber-700"> — {item.status}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {!locked && precheckReady && waiverReady && eligibleShareholders.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2.5 text-xs text-emerald-800 flex items-start space-x-2">
            <Send size={13} className="mt-0.5 flex-shrink-0 text-emerald-600" />
            <span>
              Launching sends one DocuSign envelope (Shareholder Consent, then Disclosure Statement) to{' '}
              {eligibleShareholders.length} eligible shareholder{eligibleShareholders.length === 1 ? '' : 's'}.
            </span>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-2 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ChevronRight size={13} className="mr-1 rotate-180" /> Back to Email Check
          </button>
          <button
            type="button"
            onClick={onLaunch}
            disabled={!canLaunch}
            className="inline-flex items-center px-5 py-2.5 rounded-md text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {launching ? (
              <RefreshCw size={14} className="mr-2 animate-spin" />
            ) : locked ? (
              <Lock size={14} className="mr-2" />
            ) : (
              <Send size={14} className="mr-2" />
            )}
            {launching ? 'Launching...' : locked ? 'Open Vote Room' : 'Launch Vote Room'}
          </button>
        </div>
      </div>
    </div>
  );
};
